import { env } from './env'
import { LocalEpisodeRepository } from '@/data/local/LocalEpisodeRepository'
import { LocalUserRepository } from '@/data/local/LocalUserRepository'
import { LocalOrganizationRepository } from '@/data/local/LocalOrganizationRepository'
import { LocalProgressRepository } from '@/data/local/LocalProgressRepository'
import { LocalQuizRepository } from '@/data/local/LocalQuizRepository'
import { LocalResultsRepository } from '@/data/local/LocalResultsRepository'
import { LocalXPRepository } from '@/data/local/LocalXPRepository'
import { LocalBadgeRepository } from '@/data/local/LocalBadgeRepository'
import { LocalCertificateRepository } from '@/data/local/LocalCertificateRepository'
import { LocalAnalyticsRepository } from '@/data/local/LocalAnalyticsRepository'
import { LocalSarahSessionRepository } from '@/data/local/LocalSarahSessionRepository'
import { LocalStreakRepository } from '@/data/local/LocalStreakRepository'
import { LocalStoryRepository } from '@/data/local/LocalStoryRepository'
import { LocalStoryProgressRepository } from '@/data/local/LocalStoryProgressRepository'
import { LocalEscapeRoomRepository } from '@/data/local/LocalEscapeRoomRepository'
import { LocalEscapeRoomAttemptRepository } from '@/data/local/LocalEscapeRoomAttemptRepository'
import { DevAuthProvider } from '@/services/auth/DevAuthProvider'
import { DevCertificateService } from '@/services/certificate/DevCertificateService'
import { DevAIService } from '@/services/ai/DevAIService'
import { LocalAnalyticsSink } from '@/services/analytics/LocalAnalyticsSink'
import { GamificationService } from '@/services/gamification/GamificationService'
import { StreakService } from '@/services/gamification/StreakService'
import type { AuthProvider } from '@/services/auth/AuthProvider'
import type { EpisodeRepository } from '@/repositories/EpisodeRepository'
import type { UserRepository } from '@/repositories/UserRepository'
import type { OrganizationRepository } from '@/repositories/OrganizationRepository'
import type { ProgressRepository } from '@/repositories/ProgressRepository'
import type { QuizRepository } from '@/repositories/QuizRepository'
import type { ResultsRepository } from '@/repositories/ResultsRepository'
import type { XPRepository } from '@/repositories/XPRepository'
import type { BadgeRepository } from '@/repositories/BadgeRepository'
import type { CertificateRepository } from '@/repositories/CertificateRepository'
import type { CertificateService } from '@/services/certificate/CertificateService'
import type { AnalyticsSink } from '@/services/analytics/AnalyticsSink'
import type { AnalyticsRepository } from '@/repositories/AnalyticsRepository'
import type { AIService } from '@/services/ai/AIService'
import type { SarahSessionRepository } from '@/repositories/SarahSessionRepository'
import type { StreakRepository } from '@/repositories/StreakRepository'
import type { StoryRepository } from '@/repositories/StoryRepository'
import type { StoryProgressRepository } from '@/repositories/StoryProgressRepository'
import type { EscapeRoomRepository } from '@/repositories/EscapeRoomRepository'
import type { EscapeRoomAttemptRepository } from '@/repositories/EscapeRoomAttemptRepository'

export interface AppServices {
  episodes: EpisodeRepository
  users: UserRepository
  organizations: OrganizationRepository
  progress: ProgressRepository
  quiz: QuizRepository
  results: ResultsRepository
  xp: XPRepository
  badges: BadgeRepository
  certificateRepo: CertificateRepository
  certificateService: CertificateService
  auth: AuthProvider
  analytics: AnalyticsSink
  // P2: org-scoped reporting rollups (Document 5 §11-style views), read-side
  // only — never touched by the P0/P1 write paths above.
  orgAnalytics: AnalyticsRepository
  // P3: AI Cyber Coach. `ai` is DevAIService in Zero-Cost Development Mode;
  // CloudSecureAIService (P5) implements the same interface against Azure
  // OpenAI. `sarahSessions` is the per-learner conversation store.
  ai: AIService
  sarahSessions: SarahSessionRepository
  // P4: gamification/immersive-learning repositories. All local/dev-mode
  // implementations for now; each has a matching Cosmos-backed counterpart
  // planned for P5 behind the same interface.
  streaks: StreakRepository
  stories: StoryRepository
  storyProgress: StoryProgressRepository
  escapeRooms: EscapeRoomRepository
  escapeRoomAttempts: EscapeRoomAttemptRepository
}

// Composition root (Document 11 §4/§6). This is the ONLY file in the app
// that decides which concrete implementation backs each interface. P5
// migration = add the CloudSecure* classes and extend the branches below —
// no other file changes.
function buildServices(): AppServices {
  const users = new LocalUserRepository()
  const organizations = new LocalOrganizationRepository()
  const episodes = new LocalEpisodeRepository()
  const progress = new LocalProgressRepository()
  const quiz = new LocalQuizRepository()
  const results = new LocalResultsRepository()
  const xp = new LocalXPRepository()
  const badges = new LocalBadgeRepository()
  const certificateRepo = new LocalCertificateRepository()
  const analytics = new LocalAnalyticsSink()
  const orgAnalytics = new LocalAnalyticsRepository(
    users,
    organizations,
    episodes,
    progress,
    results,
    xp,
    badges,
    certificateRepo
  )
  const sarahSessions = new LocalSarahSessionRepository()
  const streaks = new LocalStreakRepository()
  const stories = new LocalStoryRepository()
  const storyProgress = new LocalStoryProgressRepository()
  const escapeRooms = new LocalEscapeRoomRepository()
  const escapeRoomAttempts = new LocalEscapeRoomAttemptRepository()

  let auth: AuthProvider
  let certificateService: CertificateService
  if (env.authMode === 'cloudsecure') {
    throw new Error(
      'VITE_AUTH_MODE=cloudsecure is not implemented yet — CloudSecureAuthProvider ships in P5. ' +
        'Set VITE_AUTH_MODE=dev for Zero-Cost Development Mode.'
    )
  } else {
    auth = new DevAuthProvider(users)
  }

  let ai: AIService
  if (env.aiMode === 'cloudsecure') {
    throw new Error(
      'VITE_AI_MODE=cloudsecure is not implemented yet — CloudSecureAIService ships in P5. ' +
        'Set VITE_AI_MODE=dev for Zero-Cost Development Mode.'
    )
  } else {
    ai = new DevAIService()
  }
  certificateService = new DevCertificateService(certificateRepo, users)

  // GamificationService self-subscribes to the domain event bus on
  // construction (quiz.passed -> XP + badges + certificate eligibility).
  // It is intentionally not part of AppServices: nothing in the UI calls it
  // directly, it only reacts to events the UI already emits (Document 11 §4).
  // eslint-disable-next-line no-new
  new GamificationService(xp, badges, results, certificateRepo, certificateService, analytics)
  // StreakService self-subscribes the same way — reacts to the learning
  // events every P0-P4 write path already emits, no UI call site needed.
  // eslint-disable-next-line no-new
  new StreakService(streaks, users)

  return {
    episodes,
    users,
    organizations,
    progress,
    quiz,
    results,
    xp,
    badges,
    certificateRepo,
    certificateService,
    auth,
    analytics,
    orgAnalytics,
    ai,
    sarahSessions,
    streaks,
    stories,
    storyProgress,
    escapeRooms,
    escapeRoomAttempts,
  }
}

export const services = buildServices()
